import React, { useState } from 'react';
import { IAppSetting } from '../../Interfaces/IAppSetting';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import { CustomeTextField } from '../../generic/CustomeTextField';
import { IApp } from '../../Interfaces/IApp';
import { useAlert } from '../../context/AlertContext';
import { useBulkUploadAppSetting } from '../../hooks/data';

interface IAppConfigArnoTop {
  'ARNOtop.Password': string;
  'ARNOtop.Username': string;
  'ARNOtop.URL': string;
  'ARNOtop.LegalTransaction.autoSync': string;
  'ARNOtop.LegalTransaction.LegalClient.autoSync': string;
  'ARNOtop.LegalTransaction.autoDelete': string;
  'ARNOtop.LegalClient.autoSync': string;
}

function getConfigValue(
  configArray: IAppSetting[],
  key: keyof IAppConfigArnoTop,
  isBoolean: boolean = false
): string | boolean {
  const entry = configArray.find(item => item.AppKey === key);
  return entry === undefined
    ? isBoolean
      ? false
      : ''
    : isBoolean === false
      ? entry.AppValue
      : entry.AppValue === 'true';
}
function getAppSetting(
  app: IApp,
  configArray: IAppSetting[],
  setting: IAppConfigArnoTop,
  key: keyof IAppConfigArnoTop
): IAppSetting {
  let entry: IAppSetting | undefined = configArray.find(
    item => item.AppKey === key
  );

  if (entry) {
    entry = { ...entry, AppValue: String(setting[key]) };
    return entry;
  } else {
    return {
      AppKey: key,
      AppValue: String(setting[key]),
      idApp: app.idApp,
      idAppSetting: -1,
    } as IAppSetting;
  }
}

const getAppConfigArnoTop = (appSettingArray: IAppSetting[]) => {
  return {
    'ARNOtop.Password': getConfigValue(appSettingArray, 'ARNOtop.Password'),
    'ARNOtop.Username': getConfigValue(appSettingArray, 'ARNOtop.Username'),
    'ARNOtop.URL': getConfigValue(appSettingArray, 'ARNOtop.URL'),
    'ARNOtop.LegalTransaction.autoSync': getConfigValue(
      appSettingArray,
      'ARNOtop.LegalTransaction.autoSync',
      true
    ),
    'ARNOtop.LegalTransaction.autoDelete': getConfigValue(
      appSettingArray,
      'ARNOtop.LegalTransaction.autoDelete',
      true
    ),
    'ARNOtop.LegalClient.autoSync': getConfigValue(
      appSettingArray,
      'ARNOtop.LegalClient.autoSync',
      true
    ),
  } as IAppConfigArnoTop;
};

interface IProps {
  app: IApp;
  appSettingArray: IAppSetting[];
  onClose: () => void;
  onSave: () => void;
}

export const ConfigAppArnoTop: React.FC<IProps> = ({
  app,
  appSettingArray,
  onClose,
  onSave,
}) => {
  const { bulkUplaodAppSetting, isPendingBulkUploadAppSetting } =
    useBulkUploadAppSetting();
  const [appConfig, setAppConfig] = useState(
    getAppConfigArnoTop(appSettingArray)
  );

  const { showAlert } = useAlert();

  const onUpdate = async () => {
    const uploadArray = [
      getAppSetting(app, appSettingArray, appConfig, 'ARNOtop.Password'),
      getAppSetting(app, appSettingArray, appConfig, 'ARNOtop.Username'),
      getAppSetting(app, appSettingArray, appConfig, 'ARNOtop.URL'),
      getAppSetting(
        app,
        appSettingArray,
        appConfig,
        'ARNOtop.LegalTransaction.autoSync'
      ),
      getAppSetting(
        app,
        appSettingArray,
        appConfig,
        'ARNOtop.LegalTransaction.autoDelete'
      ),
      getAppSetting(
        app,
        appSettingArray,
        appConfig,
        'ARNOtop.LegalClient.autoSync'
      ),
    ];
    try {
      await bulkUplaodAppSetting(uploadArray);
      onSave();
      onClose();
    } catch (error) {
      console.error(error);
      //await refetchAll();

      showAlert({
        text: 'Fehler beim Speichern des Mandanten',
        severity: 'error',
      });
      //resetForm();
      //setIsProcessingUpdate(false);
    }
  };

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    onUpdate();
  };

  return (
    <form onSubmit={handleSubmit} autoComplete='on'>
      <img src='/img/partner/arno_top.png' height={100} />
      <Box sx={{ mt: 5 }} />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <CustomeTextField
            attr='ARNOtop.URL'
            label='URL'
            object={appConfig}
            setObject={setAppConfig}
            type='string'
            required
          />
        </Grid>
        <Grid item xs={6}>
          <CustomeTextField
            attr='ARNOtop.Username'
            label='Benutzer'
            object={appConfig}
            setObject={setAppConfig}
            type='string'
            required
          />
        </Grid>
        <Grid item xs={6}>
          <CustomeTextField
            attr='ARNOtop.Password'
            label='Passwort'
            object={appConfig}
            setObject={setAppConfig}
            type='password'
            required
          />
        </Grid>
      </Grid>

      <Card variant='outlined' sx={{ mt: 5, mb: 5 }}>
        <Typography sx={{ ml: 1 }}>Synchronisationseinstellungen</Typography>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CustomeTextField
                attr='ARNOtop.LegalTransaction.autoSync'
                label='Voränge automatisch übermitteln (an ArnoTop)'
                object={appConfig}
                setObject={setAppConfig}
                type='boolean'
              />
            </Grid>
            <Grid item xs={12}>
              <CustomeTextField
                attr='ARNOtop.LegalTransaction.autoDelete'
                label='Voränge automatisch löschen (wenn in ArnoTop gelöscht markiert wird)'
                object={appConfig}
                setObject={setAppConfig}
                type='boolean'
              />
            </Grid>
            <Grid item xs={12}>
              <CustomeTextField
                attr='ARNOtop.LegalClient.autoSync'
                label='Beteiligte automatisch übermitteln (an ArnoTop)'
                object={appConfig}
                setObject={setAppConfig}
                type='boolean'
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant='outlined' onClick={onClose}>
          Abbruch
        </Button>
        <Button
          variant='contained'
          type='submit'
          disabled={isPendingBulkUploadAppSetting}
        >
          {isPendingBulkUploadAppSetting ? (
            <Box display='flex' alignItems='center' gap={1}>
              <span>Speichern</span>
              <CircularProgress size={20} />
            </Box>
          ) : (
            'Speichern'
          )}
        </Button>
      </Box>
    </form>
  );
};
